import { Locked } from '@components/helpers/Locked';
import { Button } from '@components/ui/Button';
import { useTranslation } from 'react-i18next';
import * as Shared from '@shared/lib';

interface PlanetInfoProps {
  onClick?: () => void;
  mission?: number;
  locked?: boolean;
  name?: string;
}

export function PlanetInfo({ onClick, mission, locked, name }: PlanetInfoProps) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Planets' });

  const planet = Shared.planetData.find(d => d.name === name);
  const planetName = planet ? planet.name : 'None';
  const planetType = planet ? planet.type : 'none';
  const missionData = planet?.missions?.find((_, i) => i === mission);

  const click = () => {
    if (onClick && !locked) onClick();
  };

  return (
    <Button className={`planet-${planetName} btn border-0 border-b relative h-32`} onClick={click}>
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden">
        <img
          className="absolute inset-0 w-full h-full scale-150 object-cover"
          src={`/img/planets/${planetName.toLowerCase()}.avif`}
          alt={planetName}
        />
      </div>
      <div className="absolute inset-0 grid-overlay" />
      <div className="absolute inset-0 bg-linear-to-r from-black to-black/40" />
      {/* END Background */}

      <div className="absolute inset-0 flex justify-between">
        {/* Info */}
        <div className="h-full flex flex-col justify-between p-2 pr-8 text-left">
          <div>
            <div className="flex items-center">
              <p className="bg-shield/40 mr-1 w-5 h-5 flex items-center justify-center">
                <span className="font-bold">{t(`Types.Short.${planetType}`)}</span>
              </p>
              <p className="text-xl font-bold">
                {planetName === 'None' ? '????' : planetName.toUpperCase()}
              </p>
            </div>

            {missionData && (
              <p className="text-gray-300 text-sm">
                {t(`Missions.${missionData.type}`)}{' '}
                (<>{missionData.levelRange.from} - {missionData.levelRange.to}</>)
              </p>
            )}

            {!missionData && (
              <p className="text-gray-300 text-sm">
                {t(`${planetName}.description`)}
                {planetType === 'combat' && planet && (<> {planet.levelRange.from} - {planet.levelRange.to}</>)}
              </p>
            )}
          </div>
          <p className="text-gray-300 text-xs italic">
            "{t(`${planetName}.lore`)}"
          </p>
        </div>
        {/* END Info */}

        {/* Ascentian */}
        <div className="flex flex-col items-center justify-evenly bg-black/65 w-[36px] shrink-0 overflow-hidden">
          {planetName.slice(0, 4).split('').map((letter, index) => (
            <p className="text-2xl font-bold opacity-[25%] text-ascentian leading-none" key={index}>
              {letter}
            </p>
          ))}
        </div>
        {/* END Ascentian */}

        {/* Locked */}
        <Locked show={locked} showInfo />
        {/* END Locked */}
      </div>
    </Button>
  );
}